import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Practice, Notification, TaskFilters, TaskSortField, SortOrder } from '../types';
import { DEFAULT_DEADLINE_TIME_ZONE } from '../utils/taskStatus';

// ─── AUTH STORE ───────────────────────────────────────────────────────────────

interface AuthState {
  user: User | null;
  currentPractice: Practice | null;
  assignedPractices: Practice[]; // for platform users
  token: string | null;
  // ── THE DEADLINE DAY BASIS (founder, 2026-08-13) ────────────────────────────
  // A DATE deadline is "the LAST day to comply" on the PRACTICE's calendar — never the
  // viewer's. Both halves of this pair come from /auth/me so the client can never resolve
  // a different basis than the server did:
  //   practiceTimezone        = practice_timezone (this user's own practice; null = unset)
  //   deadlineTimezoneDefault = deadline_timezone_default (the server's RESOLVED default)
  // Both are PERSISTED: after a hard refresh the store rehydrates before /auth/me returns,
  // and a basis that is missing for those first paints is a basis that silently reverts.
  practiceTimezone: string | null;
  deadlineTimezoneDefault: string;
  // Phase A3 — true = cross-practice "Platform view" for the logged-in platform
  // user; false = practice-scoped via the practice switcher. Always false for
  // practice users (no toggle is rendered).
  platformMode: boolean;
  setUser: (user: User | null) => void;
  setCurrentPractice: (practice: Practice | null) => void;
  setAssignedPractices: (practices: Practice[]) => void;
  /** Record the /auth/me day-basis pair (see practiceTimezone above). */
  setDeadlineZoneBasis: (practiceTimezone: string | null, serverDefault?: string | null) => void;
  setToken: (token: string | null) => void;
  setPlatformMode: (v: boolean) => void;
  logout: () => void;
  isPlatformUser: () => boolean;
  // Wave 3c — capability helpers (read the roles[] UNION, mirroring the backend roleIsU). The
  // server stays authoritative (Wave 3a/3b); these drive the UI mirror only.
  isPlatformAdmin: () => boolean;
  isAccountManager: () => boolean;
  canActOnTasks: () => boolean;
}

/**
 * THE client day basis for every DATE deadline — the practice's calendar, else the server's
 * own resolved default (UTC unless DEFAULT_PRACTICE_TIMEZONE moves it, in which case /auth/me
 * ships the moved value). NEVER the viewer's zone: one case shows ONE number to an offshore
 * coordinator and a US supervisor alike. Exported as a plain selector so AppShell and
 * DoctorReviewPage — which mounts OUTSIDE the shell, straight off the approver email
 * deep-link — resolve it identically and cannot drift.
 *
 * Precedence: the SELECTED practice (a platform user switches practices, and each case is
 * judged on ITS practice's calendar) → this user's own practice → the server default.
 */
export function resolveDeadlineTimeZone(state: Pick<AuthState, 'currentPractice' | 'practiceTimezone' | 'deadlineTimezoneDefault'>): string {
  return state.currentPractice?.timezone
    || state.practiceTimezone
    || state.deadlineTimezoneDefault
    || DEFAULT_DEADLINE_TIME_ZONE;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      currentPractice: null,
      assignedPractices: [],
      token: null,
      practiceTimezone: null,
      deadlineTimezoneDefault: DEFAULT_DEADLINE_TIME_ZONE,
      platformMode: false,
      setUser: (user) => set({ user }),
      setCurrentPractice: (practice) => set({ currentPractice: practice }),
      setAssignedPractices: (practices) => set({ assignedPractices: practices }),
      setDeadlineZoneBasis: (practiceTimezone, serverDefault) =>
        set({
          practiceTimezone: practiceTimezone || null,
          deadlineTimezoneDefault: serverDefault || DEFAULT_DEADLINE_TIME_ZONE,
        }),
      setToken: (token) => set({ token }),
      setPlatformMode: (v) => set({ platformMode: v }),
      // The day basis is TENANT data: clear the practice zone on logout so the next user in
      // this browser can never read a DATE deadline on the previous practice's calendar.
      // The server default is a platform constant and survives.
      logout: () => set({ user: null, currentPractice: null, token: null, assignedPractices: [], platformMode: false, practiceTimezone: null }),
      isPlatformUser: () => {
        // Wave 3c §0 — read the roles[] UNION and include EVERY platform_* role (the prior list
        // omitted platform_account_manager, which structurally hid the switcher + admin door for
        // account managers — the linchpin bug). Single-category roles ⇒ any platform_ role = platform user.
        const u = get().user;
        if (!u) return false;
        return (u.roles ?? (u.role ? [u.role] : [])).some((r) => r.startsWith('platform_'));
      },
      // Wave 3c — the god-set holder: platform_admin MEMBERSHIP over the union (never rank).
      isPlatformAdmin: () => {
        const u = get().user;
        return !!u && (u.roles ?? (u.role ? [u.role] : [])).includes('platform_admin');
      },
      // Wave 3c — holds the per-assigned-practice Practice-Administrator capability (see lib/gating).
      isAccountManager: () => {
        const u = get().user;
        return !!u && (u.roles ?? (u.role ? [u.role] : [])).includes('platform_account_manager');
      },
      canActOnTasks: () => {
        // Track B ② — DENY gate over the roles[] UNION (backend parity: blocked iff EVERY effective
        // role is a deny role; most-permissive role wins). Single-role prod === the old two checks.
        const u = get().user;
        if (!u) return false;
        const roles = u.roles ?? (u.role ? [u.role] : []);
        if (!roles.length) return false;
        return roles.every((r) => r !== 'platform_supervisor' && r !== 'practice_doctor');
      },
    }),
    {
      name: 'previsit-auth',
      // Only persist practice selection — NOT user data or token (Auth0 handles token refresh)
      partialize: (state) => ({
        currentPractice: state.currentPractice
          ? {
              practice_id: state.currentPractice.practice_id,
              name: state.currentPractice.name,
              // The DEADLINE DAY BASIS. Persisting only {practice_id, name} meant every hard
              // refresh rehydrated a practice with NO timezone, so until /auth/me came back
              // the whole app read DATE deadlines on the wrong calendar — the null-fallback
              // bug returning silently on every reload, on the first paints an operator
              // actually looks at.
              timezone: state.currentPractice.timezone ?? null,
            }
          : null,
        practiceTimezone: state.practiceTimezone,
        deadlineTimezoneDefault: state.deadlineTimezoneDefault,
        // Persist Platform-vs-Practice mode so a refresh doesn't reset it.
        platformMode: state.platformMode,
      }),
    }
  )
);

// ─── TASK STORE ───────────────────────────────────────────────────────────────

interface TaskState {
  selectedTaskId: string | null;
  isPanelOpen: boolean;
  filters: TaskFilters;
  sortField: TaskSortField;
  sortOrder: SortOrder;
  // Phase B.1 — 'inbox' added as a fourth sibling view (Smart Inbox).
  viewMode: 'list' | 'kanban' | 'calendar' | 'inbox';
  setSelectedTask: (taskId: string | null) => void;
  openPanel: (taskId: string) => void;
  closePanel: () => void;
  setFilters: (filters: Partial<TaskFilters>) => void;
  clearFilters: () => void;
  setSort: (field: TaskSortField, order: SortOrder) => void;
  setViewMode: (mode: 'list' | 'kanban' | 'calendar' | 'inbox') => void;
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set) => ({
      selectedTaskId: null,
      isPanelOpen: false,
      filters: {},
      sortField: 'due_date',
      sortOrder: 'asc',
      viewMode: 'list',
      setSelectedTask: (taskId) => set({ selectedTaskId: taskId }),
      openPanel: (taskId) => set({ selectedTaskId: taskId, isPanelOpen: true }),
      closePanel: () => set({ isPanelOpen: false, selectedTaskId: null }),
      setFilters: (newFilters) =>
        set((state) => ({ filters: { ...state.filters, ...newFilters } })),
      clearFilters: () => set({ filters: {} }),
      setSort: (field, order) => set({ sortField: field, sortOrder: order }),
      setViewMode: (mode) => set({ viewMode: mode }),
    }),
    { name: 'previsit-tasks' }
  )
);

// ─── NOTIFICATION STORE ───────────────────────────────────────────────────────

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  unreadCount: 0,
  setNotifications: (notifications) =>
    set({
      notifications,
      unreadCount: notifications.filter((n) => !n.is_read).length,
    }),
  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
    })),
  markAsRead: (notificationId) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),
  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    })),
}));

// ─── UI STORE ─────────────────────────────────────────────────────────────────

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
    }),
    { name: 'previsit-ui' }
  )
);

// ─── DEVICE TRUST STORE ─────────────────────────────────────────────────────
// Flipped on by the axios interceptor when the API returns
// 403 { code: 'DEVICE_NOT_TRUSTED' } (only when REQUIRE_TRUSTED_DEVICE is
// enabled server-side). DeviceVerificationModal subscribes and runs the email
// OTP flow. Not persisted — it's transient request state.

interface DeviceTrustState {
  verificationRequired: boolean;
  setVerificationRequired: (v: boolean) => void;
}

export const useDeviceTrustStore = create<DeviceTrustState>()((set) => ({
  verificationRequired: false,
  setVerificationRequired: (v) => set({ verificationRequired: v }),
}));
